/**
 * Testing Schema
 * Test runs, specifications, and validation tables
 */

import { pgTable, uuid, varchar, text, timestamp, boolean, integer } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Validation Schemas
// =============================================================================
export const validationSchemas = pgTable('validation_schemas', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  specType: varchar('spec_type', { length: 100 }).notNull(),
  version: varchar('version', { length: 50 }).notNull(),
  schema: text('schema').default('{}'), // JSON
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Test Runs
// =============================================================================
export const testRuns = pgTable('test_runs', {
  id: uuid('id').primaryKey().defaultRandom(),
  testSuite: varchar('test_suite', { length: 255 }).notNull(),
  status: varchar('status', { length: 50 }).notNull(),
  totalTests: integer('total_tests'),
  passedTests: integer('passed_tests'),
  failedTests: integer('failed_tests'),
  startTime: timestamp('start_time'),
  endTime: timestamp('end_time'),
});

// =============================================================================
// Test Specifications
// =============================================================================
export const testSpecifications = pgTable('test_specifications', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  specType: varchar('spec_type', { length: 100 }).notNull(),
  version: varchar('version', { length: 50 }).notNull(),
  testCase: text('test_case').notNull(),
  expectedResult: text('expected_result').notNull(), // JSON
  actualResult: text('actual_result'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Test Validation Cases
// =============================================================================
export const testValidationCases = pgTable('test_validation_cases', {
  id: uuid('id').primaryKey().defaultRandom(),
  schemaId: uuid('schema_id'),
  inputData: text('input_data').notNull(), // JSON
  expectedResult: boolean('expected_result').notNull(),
  description: text('description'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Usability Tests
// =============================================================================
export const usabilityTests = pgTable('usability_tests', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  scenarioData: text('scenario_data').notNull(), // JSON
  targetAudience: varchar('target_audience', { length: 255 }),
  successCriteria: text('success_criteria'), // JSON
  isActive: boolean('is_active').default(true),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Specifications (Generic)
// =============================================================================
export const specifications = pgTable('specifications', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  specType: varchar('spec_type', { length: 100 }).notNull(),
  version: varchar('version', { length: 50 }).notNull(),
  schema: text('schema').default('{}'), // JSON
  ownerId: uuid('owner_id').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const validationSchemasRelations = relations(validationSchemas, ({ many }) => ({
  testCases: many(testValidationCases),
}));

export const testValidationCasesRelations = relations(testValidationCases, ({ one }) => ({
  schema: one(validationSchemas, {
    fields: [testValidationCases.schemaId],
    references: [validationSchemas.id],
  }),
}));

export const usabilityTestsRelations = relations(usabilityTests, ({ one }) => ({
  createdByUser: one(profiles, {
    fields: [usabilityTests.createdBy],
    references: [profiles.id],
  }),
}));

export const specificationsRelations = relations(specifications, ({ one }) => ({
  owner: one(profiles, {
    fields: [specifications.ownerId],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type ValidationSchema = typeof validationSchemas.$inferSelect;
export type NewValidationSchema = typeof validationSchemas.$inferInsert;
export type TestRun = typeof testRuns.$inferSelect;
export type NewTestRun = typeof testRuns.$inferInsert;
export type TestSpecification = typeof testSpecifications.$inferSelect;
export type NewTestSpecification = typeof testSpecifications.$inferInsert;
export type TestValidationCase = typeof testValidationCases.$inferSelect;
export type NewTestValidationCase = typeof testValidationCases.$inferInsert;
export type UsabilityTest = typeof usabilityTests.$inferSelect;
export type NewUsabilityTest = typeof usabilityTests.$inferInsert;
export type Specification = typeof specifications.$inferSelect;
export type NewSpecification = typeof specifications.$inferInsert;
